<script setup>
import { computed, onMounted, reactive, ref, set } from "vue";

import {
  ButtonSeverity,
  TuButton,
  TuInputSelect,
  TuInputLabel,
  TuInputCheckbox,
  TuPopover,
  isNullish,
  usePopover,
} from "@tinyurldev/tu-ui-vue";

import ValidationMessage from "@Components/forms/ValidationMessage.vue";
import BulkFormResult from "@Components/tinyurl/shortener/bulk-form/Result.vue";

import useLegacyStore from "@Composables/useLegacyStore";
import useNotify from "@Composables/useNotify";
import useShortenerForm from "@Composables/useShortenerForm";
import useLimit from "@Composables/useLimit";
import useOverage from "@Composables/useOverage";
import useTranslation from "@Composables/useTranslation";

import { getApiUrl } from "@Helpers/api/buildUrl";
import { logError } from "@Helpers/logger";

import LIMITS_TYPE from "@Constants/limits-type";
import { OVERAGE_STATUS } from "@Constants/overage-status";

import { buildDomainsInput } from "../single-form/form-helper";

const { __ } = useTranslation();
const { allowedAvailableDomains, addDomain, defaultDomain } = useShortenerForm();
const { notifySuccess } = useNotify();

const isPopoverVisible = ref(false);
const { referenceRef, popoverRef } = usePopover(isPopoverVisible, {
  placement: "bottom",
});

const { getters } = useLegacyStore();
const linksLimit = useLimit(LIMITS_TYPE.LINKS_PER_MONTH);
const linksOverage = useOverage(LIMITS_TYPE.LINKS_PER_MONTH);

const isFileSizeValid = ref(true);
const bulkStatus = ref(null);
const bulkUrlForm = reactive(
  new SparkForm({
    file: null,
    domain: "",
    withHeader: true,
  }),
);

const isAccountUsageEnabled = computed(() => getters.isAccountUsageEnabled);
const isFormBusy = computed(() => bulkUrlForm.busy);
const isUserSubscribed = computed(() => getters.isUserSubscribed);
const currentSubscription = computed(() => getters.getUserCurrentSubscription);
const isFeatureLocked = computed(() => {
  if (!isAccountUsageEnabled.value) {
    return false;
  }

  if (
    !isUserSubscribed.value ||
    currentSubscription.value?.isLegacy
  ) {
    return linksLimit.isHardLimitReached.value;
  }

  return (
    linksLimit.isHardLimitReached.value &&
    !linksLimit.hasStatus(OVERAGE_STATUS.ALL_ENABLE)
  );
});

const domainsProperties = computed(() => {
  const copyProperties = {
    help: null,
    errorMessage: bulkUrlForm.errors.get("domain"),
    isHtml: false,
  };
  const domainOptions = allowedAvailableDomains.value?.map((domain) => {
    return {
      value: domain.domain,
      name: domain.domain,
    };
  });
  const inputProperties = {
    isDisabled: isFeatureLocked.value || isFormBusy.value,
  };

  return buildDomainsInput({
    copyProperties,
    inputProperties,
    domainOptions,
  });
});

function startNewBatch() {
  set(bulkUrlForm, "file", null);
  bulkStatus.value = null;
}

function processFile(event) {
  bulkUrlForm.file = event.target.files[0];

  const megabyte = 1024 * 1024;
  const maxSize = megabyte * 10;

  if (bulkUrlForm.file && bulkUrlForm.file.size > maxSize) {
    isFileSizeValid.value = false;
    bulkUrlForm.setErrors({
      file: [__("shortener.bulk_upload_file_size_exceeds", { size: "10MB" })],
    });
  } else {
    isFileSizeValid.value = true;
    const errors = { ...bulkUrlForm.errors.all() };
    delete errors.file;
    bulkUrlForm.setErrors(errors);
  }
}

async function bulkCreate() {
  const formData = new FormData();
  formData.append("file", bulkUrlForm.file);
  formData.append("domain", bulkUrlForm.domain);
  formData.append("withHeader", bulkUrlForm.withHeader);

  bulkUrlForm.startProcessing();

  try {
    const requestUrl = getApiUrl("bulk/csv");
    const requestPayload = formData;
    const requestConfig = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    const { data } = await axios.post(
      requestUrl,
      requestPayload,
      requestConfig,
    );

    bulkUrlForm.finishProcessing();
    bulkStatus.value = data;
    notifySuccess({ text: __("shortener.bulk_upload_submit_feedback") });
  } catch (errors) {
    bulkUrlForm.setErrors(errors.response.data.errors);
    logError("Bulk tinyurls upload fails: " + errors);
  }
}

onMounted(() => {
  bulkUrlForm.domain = defaultDomain();
});
</script>

<template>
  <BulkFormResult
    v-if="!isNullish(bulkStatus)"
    :bulk-status="bulkStatus"
    test-id-prefix="dashboard"
    @on-start-new-batch="startNewBatch"
  />

  <form
    v-else
    autocomplete="off"
    @submit.prevent="bulkCreate"
  >
    <TuPopover
      v-show="isPopoverVisible"
      ref="popoverRef"
      class="bulk-form__file-upload-popover"
    >
      <p class="font-weight-semibold">
        Please follow the guide accordingly.
      </p>
      <img
        src="@Images/home/bulk-guide-personal.png"
        class="bulk-form__file-upload-img"
        alt="Bulk upload guide figure"
      >
      <p class="flex-center mb-0">
        <i
          class="fa-solid fa-circle fa-fw mt-1 mr-1 d-none"
          style="font-size: 6px"
        />
        <span class="font-weight-semibold">Accepted file type</span>:
        csv, xls, xlsx, ods
      </p>
      <p class="flex-center mb-0">
        <i
          class="fa-solid fa-circle fa-fw mt-1 mr-1 d-none"
          style="font-size: 6px"
        />
        <span class="font-weight-semibold">Maximum file size</span>: 10MB
      </p>
    </TuPopover>

    <!-- File Upload -->
    <div class="bulk-form__file-upload">
      <div class="bulk-form__file-upload-label">
        <TuInputLabel
          for="file-input"
          label="Attach File"
        />
        <div
          ref="referenceRef"
          class="bulk-form__file-upload-icon"
          @mouseenter="isPopoverVisible = true"
          @mouseleave="isPopoverVisible = false"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d="M6 12C7.5913 12 9.11742 11.3679 10.2426 10.2426C11.3679 9.11742 12 7.5913 12 6C12 4.4087 11.3679 2.88258 10.2426 1.75736C9.11742 0.632141 7.5913 0 6 0C4.4087 0 2.88258 0.632141 1.75736 1.75736C0.632141 2.88258 0 4.4087 0 6C0 7.5913 0.632141 9.11742 1.75736 10.2426C2.88258 11.3679 4.4087 12 6 12ZM5.0625 7.875H5.625V6.375H5.0625C4.75078 6.375 4.5 6.12422 4.5 5.8125C4.5 5.50078 4.75078 5.25 5.0625 5.25H6.1875C6.49922 5.25 6.75 5.50078 6.75 5.8125V7.875H6.9375C7.24922 7.875 7.5 8.12578 7.5 8.4375C7.5 8.74922 7.24922 9 6.9375 9H5.0625C4.75078 9 4.5 8.74922 4.5 8.4375C4.5 8.12578 4.75078 7.875 5.0625 7.875ZM6 3C6.19891 3 6.38968 3.07902 6.53033 3.21967C6.67098 3.36032 6.75 3.55109 6.75 3.75C6.75 3.94891 6.67098 4.13968 6.53033 4.28033C6.38968 4.42098 6.19891 4.5 6 4.5C5.80109 4.5 5.61032 4.42098 5.46967 4.28033C5.32902 4.13968 5.25 3.94891 5.25 3.75C5.25 3.55109 5.32902 3.36032 5.46967 3.21967C5.61032 3.07902 5.80109 3 6 3Z"
              fill="#727272"
            />
          </svg>
        </div>
      </div>
      <div
        class="bulk-form__file-upload-input"
        :class="[{ 'has-error': bulkUrlForm.errors.has('file') }]"
      >
        <div class="bulk-form__file-upload-input-wrapper">
          <input
            id="file-input"
            type="file"
            accept=".csv, text/csv, .xlsx, .ods"
            :disabled="isFeatureLocked || isFormBusy"
            @change="processFile"
          >
        </div>
      </div>
      <ValidationMessage
        class="bulk-form__file-upload-validation"
        data-test-id="dashboard_create_tinyurl_validation_file"
        :message="bulkUrlForm.errors.get('file')"
      />
    </div>

    <!-- Header Row -->
    <TuInputCheckbox
      id="keep-logged"
      v-model="bulkUrlForm.withHeader"
      class="bulk-form__header-row"
      label="File includes a header row"
      :is-disabled="isFormBusy"
    />

    <!-- Domain and Alias Field -->
    <TuInputSelect
      v-model="bulkUrlForm.domain"
      v-bind="domainsProperties"
      class="bulk-form__domain-field"
      @bottom-clicked="addDomain"
    />

    <!-- Shorten URL Button -->
    <TuButton
      class="bulk-form__submit-button"
      data-test-id="home_shortener_btn_create"
      :label="$t('shorten_link')"
      :is-block="true"
      :is-disabled="isFeatureLocked || isFormBusy || !isFileSizeValid"
      :severity="ButtonSeverity.SECONDARY"
      :is-loading="isFormBusy"
      @click="bulkCreate"
    />
  </form>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.bulk-form {
  $self: &;

  &__file-upload-label {
    display: flex;
    align-items: flex-start;
    margin-bottom: spacing.$xxxs;
  }

  &__file-upload-guide {
    display: flex;
    align-items: flex-start;
    position: relative;
    margin-left: spacing.$xs-5;
  }

  &__file-upload-icon {
    display: flex;
    margin-left: spacing.$xs-5;
  }

  &__file-upload-popover {
    z-index: 1;
    font-size: typography.$s-font-size;
    padding: spacing.$xxs;
    max-width: 500px;
    width: 100%;
  }

  &__file-upload-img {
    width: 100%;
    margin-bottom: spacing.$xs-5;
  }

  &__file-upload-input {
    background-color: color.$text-white;
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='5' ry='5' stroke='%23DEE2E6FF' stroke-width='1' stroke-dasharray='8' stroke-dashoffset='5' stroke-linecap='square'/%3e%3c/svg%3e");
    border-radius: border.$small-radius;
    margin-bottom: spacing.$xxxs;
    padding: spacing.$xxs;

    &-wrapper {
      overflow: hidden;
    }

    input {
      width: 100%;

      &:focus-visible {
        outline: none;
        outline-offset: 0;
        box-shadow: none;
      }
    }

    &:has(input:focus) {
      box-shadow: 0 0 0 0.2rem color.$secondary-light;
    }

    &.has-error {
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='5' ry='5' stroke='%23C31818FF' stroke-width='1' stroke-dasharray='8' stroke-dashoffset='5' stroke-linecap='square'/%3e%3c/svg%3e");
    }
  }
}
</style>
